import debounce from 'lodash.debounce';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import {
  useQuery,
  useMutation,
  useClient,
  OperationContext,
  AnyVariables,
  UseQueryArgs,
  UseQueryResponse,
  DocumentInput,
  UseMutationResponse,
  UseQueryExecute,
  UseMutationState,
  OperationResult,
  RequestPolicy,
} from 'urql';

import { useHandleUserFacingError } from '../utils';

const DEBOUNCE_INTERVAL = 500;

const queryContext: Partial<OperationContext> = {
  fetchOptions: {
    credentials: 'include',
  },
};
type SecureGraphqlQueryResponse<D, V extends AnyVariables, T extends string> = [
  Omit<UseQueryResponse<D, V>[0], 'error'> & { errorMessage?: T },
  UseQueryResponse<D, V>[1]
];

const useSecureGraphqlQuery = <D, V extends AnyVariables = AnyVariables, T extends string = never>(
  args: Omit<UseQueryArgs<V, D>, 'context' | 'requestPolicy'>,
  handledErrors: T[] = []
): SecureGraphqlQueryResponse<D, V, T> => {
  const [{ error, ...restData }, executeQuery] = useQuery<D, V>({
    ...args,
    requestPolicy: 'cache-and-network',
    context: queryContext,
  } as UseQueryArgs<V, D>);
  const errorMessage = useHandleUserFacingError(error, handledErrors);

  return [{ errorMessage, ...restData }, executeQuery];
};

type SecureGraphqlMutationResponse<D, V extends AnyVariables, T extends string> = [
  Omit<UseMutationState<D, V>, 'error'> & { errorMessage?: T },
  UseMutationResponse<D, V>[1]
];

const useSecureGraphqlMutation = <D, V extends AnyVariables = AnyVariables, T extends string = never>(
  input: DocumentInput<D, V>,
  handledErrors: T[] = [],
  context?: Partial<OperationContext>
): SecureGraphqlMutationResponse<D, V, T> => {
  const [{ error, ...restData }, execute] = useMutation<D, V>(input);

  const executeMutation = useCallback(
    (variables: V) =>
      execute(variables, {
        ...context,
        ...queryContext,
      }),
    [execute, queryContext]
  );

  const errorMessage = useHandleUserFacingError(error, handledErrors);

  return [{ errorMessage, ...restData }, executeMutation];
};

const useSecureGraphqlLazyQuery = <D, V extends AnyVariables = AnyVariables>(
  input: DocumentInput<D, V>,
  requestPolicy?: RequestPolicy
) => {
  const client = useClient();

  return useCallback(
    (variables: V): Promise<OperationResult<D, V>> =>
      client
        .query<D, V>(input, variables, { ...queryContext, ...(requestPolicy ? { requestPolicy } : {}) })
        .toPromise(),
    [client, input, requestPolicy]
  );
};

const useReexecuteQueryWhenInputsChange = (reexecuteQuery: UseQueryExecute, inputs: unknown) => {
  const debouncedReexecute = useMemo(() => debounce(reexecuteQuery, DEBOUNCE_INTERVAL), [reexecuteQuery]);
  const query = useRef(debouncedReexecute);
  useEffect(() => {
    query.current = debouncedReexecute;
  }, [debouncedReexecute]);

  useEffect(() => {
    query.current();
  }, [inputs]);
};

export {
  useReexecuteQueryWhenInputsChange,
  useSecureGraphqlLazyQuery,
  useSecureGraphqlMutation,
  useSecureGraphqlQuery,
};
